import React, { useEffect, useState } from 'react';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { BarChart } from 'echarts/charts';
import {
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { useAppContext } from 'providers/AppProvider';
import { tooltipFormatterDefault } from 'helpers/echart-utils';
import { fetchRiskEvolution } from 'services/hvarisk.service';

echarts.use([
  BarChart,
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent,
  CanvasRenderer
]);

interface YearlyRiskChartProps {
  selectedYear: number;
}

const YearlyRiskChart: React.FC<YearlyRiskChartProps> = ({ selectedYear }) => {
  const {
    config: { isDark },
    getThemeColor
  } = useAppContext();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [chartData, setChartData] = useState<any>({
    months: [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec'
    ],
    critical: [],
    high: []
  });

  useEffect(() => {
    const fetchYearlyData = async () => {
      try {
        // Obtener datos del año seleccionado
        const startDate = `${selectedYear}-01-01`;
        const endDate = `${selectedYear}-12-31`;

        const yearData = await fetchRiskEvolution({
          datefrom: startDate,
          dateto: endDate,
          interval: 'monthly'
        });
        console.log('Year data received:', yearData);

        // Inicializar arrays con 12 meses
        const critical = new Array(12).fill(0);
        const high = new Array(12).fill(0);

        // Llenar los datos por mes
        yearData.forEach(item => {
          const date = new Date(item.date);
          const monthIndex = date.getMonth(); // 0-11

          critical[monthIndex] = item.critical || 0;
          high[monthIndex] = item.high || 0;
        });

        setChartData({
          months: [
            'Jan',
            'Feb',
            'Mar',
            'Apr',
            'May',
            'Jun',
            'Jul',
            'Aug',
            'Sep',
            'Oct',
            'Nov',
            'Dec'
          ],
          critical,
          high
        });
      } catch (error) {
        console.error('Error fetching yearly risk data:', error);
        // Mantener estructura vacía en caso de error
        setChartData({
          months: [
            'Jan',
            'Feb',
            'Mar',
            'Apr',
            'May',
            'Jun',
            'Jul',
            'Aug',
            'Sep',
            'Oct',
            'Nov',
            'Dec'
          ],
          critical: new Array(12).fill(0),
          high: new Array(12).fill(0)
        });
      }
    };

    fetchYearlyData();
  }, [selectedYear]);

  const option = {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow'
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      formatter: (params: any) =>
        tooltipFormatterDefault(params, 'MMM YYYY', 'color'),
      backgroundColor: !isDark
        ? 'rgba(240, 240, 240, 0.9)'
        : 'rgba(49, 55, 74, 0.9)',
      textStyle: {
        color: !isDark ? '#202020' : '#ccc',
        fontFamily: 'Nunito Sans',
        fontSize: 14
      },
      borderColor: !isDark
        ? 'rgba(49, 55, 74, 0.4)'
        : 'rgba(140, 140, 180, 0.8)',
      borderWidth: 1,
      padding: [7, 10],
      transitionDuration: 0
    },
    legend: {
      data: ['Critical (5)', 'High (4)'],
      bottom: 0,
      itemWidth: 16,
      itemHeight: 8,
      itemGap: 10,
      inactiveColor: getThemeColor('quaternary-color'),
      inactiveBorderWidth: 0,
      textStyle: {
        color: getThemeColor('body-color'),
        fontWeight: 500,
        fontSize: 12,
        fontFamily: 'Nunito Sans'
      },
      icon: 'roundRect'
    },
    grid: {
      left: '3%',
      right: '0%',
      bottom: '15%',
      top: '5%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: chartData.months,
      axisTick: {
        show: true,
        alignWithLabel: true,
        lineStyle: {
          color: !isDark ? 'rgba(140, 140, 180, 0.8)' : 'rgba(49, 55, 74, 0.4)'
        }
      },
      axisLine: {
        lineStyle: {
          color: !isDark ? 'rgba(140, 140, 180, 0.8)' : 'rgba(49, 55, 74, 0.4)'
        }
      },
      axisLabel: {
        color: getThemeColor('body-color'),
        fontFamily: 'Nunito Sans',
        fontSize: 11
      },
      splitLine: {
        show: false
      }
    },
    yAxis: {
      type: 'value',
      name: 'Number of Risks',
      nameTextStyle: {
        color: getThemeColor('body-color'),
        fontFamily: 'Nunito Sans',
        fontSize: 12
      },
      axisLine: {
        show: true,
        lineStyle: {
          color: getThemeColor('tertiary-bg')
        }
      },
      axisTick: {
        show: true,
        lineStyle: {
          color: getThemeColor('tertiary-bg')
        }
      },
      axisLabel: {
        color: getThemeColor('body-color'),
        fontFamily: 'Nunito Sans',
        fontSize: 9,
        formatter: '{value}'
      },
      splitLine: {
        show: true,
        lineStyle: {
          color: getThemeColor('tertiary-bg'),
          type: 'dashed'
        }
      }
    },
    toolbox: {
      itemSize: 10,
      showTitle: false,
      show: true,
      iconStyle: {
        // eslint-disable-next-line
            borderColor: !isDark? 'rgba(160, 160, 190, 0.9)': 'rgba(100, 100, 150, 0.9)'
      },
      emphasis: {
        iconStyle: { borderColor: getThemeColor('primary') }
      },
      feature: {
        mark: { show: true },
        magicType: { show: false, type: ['line', 'bar'] },
        dataZoom: { show: false },
        dataView: { show: false, readOnly: true },
        restore: { show: false },
        saveAsImage: { show: true }
      }
    },
    series: [
      {
        name: 'Critical (5)',
        type: 'bar',
        stack: 'risks',
        data: chartData.critical,
        itemStyle: {
          color: 'rgba(179, 0, 0, 0.9)'
        }
      },
      {
        name: 'High (4)',
        type: 'bar',
        stack: 'risks',
        data: chartData.high,
        itemStyle: {
          color: 'rgba(255, 155, 40, 0.87)'
        }
      }
    ]
  };

  return (
    <ReactEChartsCore
      echarts={echarts}
      option={option}
      style={{ height: '150px' }}
      notMerge={true}
      lazyUpdate={true}
    />
  );
};

export default YearlyRiskChart;
